import * as React from 'react';
import { ActionDropdown } from '../../Dropdown/dropdown';
import './actionMenu.scss';
import { ViewType } from '../../../enums/ViewType';
import { NodeType } from '../../../enums/NodeType';
import { UserSettingProps } from '../../../types/UserSetting';
import { useActions } from '../viewActions';
import { AppRefId } from '../../../utils/constants';
import { findAll } from 'rcd-cpp-workspace/utils/array';
import { useSelector } from '../../../utils/useSelector';
import { deepEquals } from 'rcd-cpp-workspace/utils/deepEquals';
import { ActAsModal, ViewAsModal } from '../ViewAsModal/viewAsModal';
import { BacktagModal } from '../BacktagModal/backtagModal';
import { Role } from '../../../enums/Role';
import { AdminDomain } from '../../../enums/AdminDomain';
import { KanbanColumnWidth } from '../../../enums/KanbanColumnWidth';
import { ToasterWithLink, useToaster } from '../../Toaster/toaster';
import { ImportManagedDataModal } from '../ImportManagedDataModal/importManagedDataModal';
import { ViewCategory } from '../../../enums/viewCategory';

export enum Action {
  collapseSwimlanes = 'Collapse All Swimlanes',
  showSwimlanes = 'Show Swimlanes',
  hideSwimlanes = 'Hide Swimlanes',
  showProgress = 'Show Progress',
  hideProgress = 'Hide Progress',
  showLegend = 'Show Legend',
  hideLegend = 'Hide Legend',
  expandCards = 'Expand Cards',
  collapseCards = 'Collapse Cards',
  expandColumns = 'Expand Columns',
  collapseColumns = 'Collapse Columns',
  addTags = 'Add Tags',
  viewAs = 'View As',
  actAs = 'Act As',
  importManagedData = 'Import',
  exportRolePriorityPosition = 'Export'
}

const getViewSettings = (action: Action): UserSettingProps => {
  switch (action) {
    case Action.showProgress:
      return { hideItemProgress: false };
    case Action.hideProgress:
      return { hideItemProgress: true };
    case Action.showSwimlanes:
      return { hideSwimlanes: false };
    case Action.hideSwimlanes:
      return { hideSwimlanes: true };
    case Action.addTags:
    case Action.collapseSwimlanes:
    case Action.actAs:
    case Action.exportRolePriorityPosition:
    case Action.viewAs:
    case Action.importManagedData:
      return {};
    case Action.expandCards:
      return { hideCardDetails: false };
    case Action.collapseCards:
      return { hideCardDetails: true };
    case Action.expandColumns:
      return { boardColumnSize: KanbanColumnWidth.Expanded };
    case Action.collapseColumns:
      return { boardColumnSize: KanbanColumnWidth.Collapsed };
    case Action.showLegend:
      return { hideProgressLegend: false };
    case Action.hideLegend:
      return { hideProgressLegend: true };
    default:
      return null;
  }
};

type Props = { showCheckbox?: boolean };

export const ActionMenu = ({ showCheckbox }: Props) => {
  const [showViewAsModal, setShowViewAsModal] = React.useState(false);
  const [showActAsModal, setShowActAsModal] = React.useState(false);
  const [showBacktagModal, setShowBacktagModal] = React.useState(false);
  const [showImportManagedDataModal, setShowImportManagedDataModal] =
    React.useState(false);
  const { setViewSettings, toggleSwimlane, exportRolePriorityTree } =
    useActions();
  const { createToaster } = useToaster(ToasterWithLink);
  const hasItemChecked = useSelector(
    s => s.view?.isCheckedAll || s.view?.toggledRefIds?.length > 0
  );
  const editMode = useSelector(s => s.view?.editMode);
  const workspace = useSelector(s => s.planning?.selectedWorkspace);
  const isSystemWorkspace = workspace?.labels?.includes(
    NodeType.AdminWorkspace
  );
  const actions = useSelector(s => {
    const userSettings = s.planning?.userSettings;
    if (!userSettings) return null;
    const view = s.view?.selected;
    if (!view?.props) return null;
    const viewSetting = userSettings[view.props.refId];
    const appSetting = userSettings[AppRefId];
    const expandedSwimlanes = findAll(s.view?.swimlanes, { expanded: true });
    const isManagedData =
      view?.category == ViewCategory.ManagedData &&
      (view?.schema?.props?.type == NodeType.PriorityJourneyStep ||
        view?.schema?.props?.type == NodeType.RolePriority ||
        view?.schema?.props?.type == NodeType.Position);
    const isEnterpriseCategoryOrPerennialPriority =
      view?.category == ViewCategory.EnterpriseData &&
      (view?.schema?.props?.type == NodeType.TopicCategory ||
        view?.schema?.props?.type == NodeType.TaxonomyTag);

    const actions: Action[] = [];
    if (view?.props?.groupBy?.length > 1) {
      if (viewSetting?.props?.hideSwimlanes) {
        actions.push(Action.showSwimlanes);
      } else {
        actions.push(Action.hideSwimlanes);
        if (expandedSwimlanes?.length) {
          actions.push(Action.collapseSwimlanes);
        }
      }
    }
    if (
      view.props.data == NodeType.Item &&
      view.props.type == ViewType.Kanban
    ) {
      if (viewSetting?.props?.hideCardDetails) {
        actions.push(Action.expandCards);
      } else {
        actions.push(Action.collapseCards);
      }
      if (viewSetting?.props?.boardColumnSize == KanbanColumnWidth.Expanded) {
        actions.push(Action.collapseColumns);
      } else {
        actions.push(Action.expandColumns);
      }
      if (viewSetting?.props?.hideItemProgress) {
        actions.push(Action.showProgress);
      } else {
        actions.push(Action.hideProgress);
        if (appSetting?.props?.hideProgressLegend) {
          actions.push(Action.showLegend);
        } else {
          actions.push(Action.hideLegend);
        }
      }
    }
    if (view.isMyItemsView && !s.view?.backingUpFor && !editMode) {
      if (s.app?.user?.roles?.includes(Role.admin)) {
        actions.push(Action.actAs);
      }
      actions.push(Action.viewAs);
    }
    if (
      view.props.type == ViewType.Tabular ||
      view.id == AdminDomain.stagedTags
    ) {
      if (view.isBacktaggingView && showCheckbox) {
        actions.push(Action.addTags);
      }
      if (
        isSystemWorkspace &&
        (isManagedData || isEnterpriseCategoryOrPerennialPriority)
      ) {
        if (
          s.app?.user?.roles?.includes(Role.admin) ||
          s.app?.user?.roles?.includes(Role.backtagAdmin)
        ) {
          actions.push(Action.exportRolePriorityPosition);
          if (process.env.ENV != 'prod') {
            actions.push(Action.importManagedData);
          }
        }
      }
    }

    return actions;
  }, deepEquals);
  if (!actions || !actions.length) return null;

  return (
    <>
      <ActionDropdown
        position="right"
        options={actions}
        disabled={a => a == Action.addTags && !hasItemChecked}
        onSelect={a => {
          const settings = getViewSettings(a);
          if (settings) {
            switch (a) {
              case Action.collapseSwimlanes:
                toggleSwimlane(null, false);
                break;
              case Action.showLegend:
              case Action.hideLegend:
                setViewSettings(settings, AppRefId);
                break;
              case Action.viewAs:
                setShowViewAsModal(true);
                break;
              case Action.actAs:
                setShowActAsModal(true);
                break;
              case Action.addTags:
                setShowBacktagModal(true);
                break;
              case Action.exportRolePriorityPosition:
                exportRolePriorityTree(createToaster());
                break;
              case Action.importManagedData:
                setShowImportManagedDataModal(true);
                break;
              default:
                setViewSettings(settings);
                break;
            }
          }
        }}
      />
      {showViewAsModal && (
        <ViewAsModal onClose={() => setShowViewAsModal(false)} />
      )}
      {showActAsModal && (
        <ActAsModal onClose={() => setShowActAsModal(false)} />
      )}
      {showBacktagModal && (
        <BacktagModal onClose={() => setShowBacktagModal(false)} />
      )}
      {showImportManagedDataModal && (
        <ImportManagedDataModal
          onClose={() => setShowImportManagedDataModal(false)}
        />
      )}
    </>
  );
};
